import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { Grenzen } from '../lib/tarife'

export interface User {
  id: string
  email: string
  role: 'user' | 'pro' | 'admin'
  is_admin: boolean
  is_pro: boolean
  first_name?: string
  last_name?: string
  is_active: boolean
  /** Stufe der Bezahlschranke, vom Backend (tarife.py). Fehlt in alten gespeicherten Profilen. */
  grenzen?: Grenzen
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  isAdmin: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// API Base URL - entferne trailing slash falls vorhanden
const getApiBase = () => {
  const envUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'
  // Entferne trailing slash und /api falls vorhanden, da wir es später hinzufügen
  return envUrl.replace(/\/api\/?$/, '').replace(/\/$/, '')
}

const API_BASE = getApiBase()

// Wie lange die Seite beim Laden hoechstens auf die Pruefung der Anmeldung wartet.
const PRUEF_FRIST_MS = 5000

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Beim Laden: gespeicherte Anmeldung holen UND gegen das Backend pruefen
  // (13.09.2026). Das Token laeuft nach sieben Tagen ab, und Rolle oder Sperre
  // koennen sich seitdem geaendert haben. Bis die Antwort da ist, bleibt
  // isLoading stehen -- sonst schickte die Seite mit dem alten Token Anfragen
  // los und zeigte kurz ein Menue, das nicht mehr gilt. Bei Netzfehler oder
  // nach Ablauf der Frist bleibt die Anmeldung stehen; dann klaert es die
  // naechste Anfrage.
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('auth_user')
    if (!storedToken || !storedUser) {
      setIsLoading(false)
      return
    }

    let gespeichert: User
    try {
      gespeichert = JSON.parse(storedUser)
    } catch (e) {
      console.error('Failed to parse stored user', e)
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      setIsLoading(false)
      return
    }

    let vorbei = false
    const abbruch = new AbortController()
    const frist = window.setTimeout(() => abbruch.abort(), PRUEF_FRIST_MS)

    const uebernehmen = (nutzer: User) => {
      if (vorbei) return
      setToken(storedToken)
      setUser(nutzer)
    }

    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${storedToken}` },
      signal: abbruch.signal,
    })
      .then(async (antwort) => {
        if (antwort.status === 401 || antwort.status === 403) {
          // Abgelaufen oder gesperrt: Anmeldung verwerfen.
          if (!vorbei) {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_user')
          }
          return
        }
        if (!antwort.ok) {
          uebernehmen(gespeichert)
          return
        }
        // Frische Rolle -- ein herabgestuftes Konto verliert hier das Admin-Menue.
        const frisch = (await antwort.json()) as User
        if (!vorbei) localStorage.setItem('auth_user', JSON.stringify(frisch))
        uebernehmen(frisch)
      })
      .catch(() => uebernehmen(gespeichert))
      .finally(() => {
        window.clearTimeout(frist)
        if (!vorbei) setIsLoading(false)
      })

    return () => {
      vorbei = true
      window.clearTimeout(frist)
      abbruch.abort()
    }
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const loginUrl = `${API_BASE}/api/auth/login`
      console.log('Login attempt to:', loginUrl)
      console.log('API_BASE:', API_BASE)

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      console.log('Login response status:', response.status)
      console.log('Login response headers:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        let errorMessage = 'Login fehlgeschlagen'
        try {
          const errorData = await response.text()
          console.log('Error response body:', errorData)
          if (errorData) {
            try {
              const parsedError = JSON.parse(errorData)
              errorMessage = parsedError.detail || errorMessage
            } catch {
              errorMessage = `${errorMessage}: ${errorData.substring(0, 200)}`
            }
          }
        } catch (e) {
          console.error('Could not parse error response:', e)
          errorMessage = `HTTP ${response.status}: ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      console.log('Login successful, user data:', data.user)
      setToken(data.access_token)
      setUser(data.user)

      // Token und User in localStorage speichern
      localStorage.setItem('auth_token', data.access_token)
      localStorage.setItem('auth_user', JSON.stringify(data.user))
    } catch (error: any) {
      console.error('Login error:', error)
      // Bessere Fehlermeldung für Netzwerk-Fehler
      if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
        throw new Error('Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut.')
      }
      throw error
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
  }

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.is_admin || false,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

